'use client';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import clsx from 'clsx';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';

import { neutral4 } from '../../colors';
import { shadowLevel3 } from '../../shadows';
import Popper from '../Popper';

const placements = {
  'bottom-end': 'top right',
  'bottom-start': 'top left',
  bottom: 'top center',
  'left-end': 'bottom right',
  'left-start': 'top right',
  left: 'right center',
  'right-end': 'bottom left',
  'right-start': 'top left',
  right: 'left center',
  'top-end': 'bottom right',
  'top-start': 'bottom left',
  top: 'bottom center',
};

const PopperMenu = ({
  children,
  id,
  open,
  onClose = noop,
  anchorEl,
  placement = 'bottom-start',
  disablePortal = false,
  disableTransition = false,
  classes = {},
  className = '',
  PaperProps = {},
  MenuListProps = {},
  ClickAwayProps = {},
  TransitionProps: CustomTransitionProps = {},
  TransitionComponent = Grow,
  disableClickAwayListener,
  sx,
  ...rest
}) => {
  const handleListKeyDown = (event) => {
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      anchorEl.focus();
      onClose && onClose(event);
    }
  };

  const menu = (
    <MenuList
      id={id}
      autoFocus
      onKeyDown={handleListKeyDown}
      {...MenuListProps}
      className={clsx(classes.list, MenuListProps.className)}
    >
      {children}
    </MenuList>
  );

  const content = (
    <Paper {...PaperProps} className={clsx(classes.paper, PaperProps.className)}>
      {disableClickAwayListener ? (
        menu
      ) : (
        <ClickAwayListener onClickAway={onClose} {...ClickAwayProps}>
          {menu}
        </ClickAwayListener>
      )}
    </Paper>
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      disablePortal={disablePortal}
      transition={!disableTransition}
      className={clsx(classes.root, className)}
      {...rest}
      sx={[
        {
          zIndex: 3001,
          '.MuiPaper-root': {
            boxShadow: shadowLevel3,
            borderColor: neutral4,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {disableTransition
        ? content
        : ({ TransitionProps, placement }) => {
            return (
              <TransitionComponent
                {...TransitionProps}
                {...CustomTransitionProps}
                sx={{
                  transformOrigin: placements[placement],
                  ...PaperProps?.sx,
                  ...TransitionProps?.sx,
                  ...CustomTransitionProps?.sx,
                }}
              >
                {content}
              </TransitionComponent>
            );
          }}
    </Popper>
  );
};

PopperMenu.propTypes = {
  /**
   * A HTML element, or a function that returns it. It's used to set the
   * position of the popper.
   */
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** CSS classes applied to the internal components. */
  classes: PropTypes.shape({
    list: PropTypes.string,
    paper: PropTypes.string,
    root: PropTypes.string,
  }),
  /** Classes applied to the root `Popper` component. */
  className: PropTypes.string,
  /** Props forwarded to the `ClickAwayListener` component. */
  ClickAwayProps: PropTypes.object,
  /** If `true`, the children will be under the DOM hierarchy of the parent component. */
  disablePortal: PropTypes.bool,
  /** If `true`, list will render without a transition. */
  disableTransition: PropTypes.bool,
  /** ID of the menu. */
  id: PropTypes.string,
  /** Props forwarded to the `MenuList` component. */
  MenuListProps: PropTypes.object,
  /** Callback fired when the component requests to be closed. */
  onClose: PropTypes.func,
  /** If `true`, the menu is visible. */
  open: PropTypes.bool,
  /** Props forwarded to the `Paper` component. */
  PaperProps: PropTypes.object,
  /** Placement of the `Popper`. */
  placement: PropTypes.oneOf([
    'auto-end',
    'auto-start',
    'auto',
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  /**
   * Transition component that wraps the `MenuList`.
   *
   * @see https://mui.com/material-ui/transitions/
   */
  TransitionComponent: PropTypes.elementType,
  /**
   * Props forwarded to the transition component specified by `TransitionComponent`.
   */
  TransitionProps: PropTypes.object,
};

export default PopperMenu;
